<template>
  <nav ref="menu" class="section-menu" :aria-label="label">
    <button
      v-for="g in sections"
      :key="g.id"
      class="section-menu-group"
      :class="{ open: openGroup === g.id, pinned: pinnedGroup === g.id }"
      :data-group-id="g.id"
      type="button"
      :title="g.title || g.label"
      :aria-haspopup="'menu'"
      :aria-expanded="(openGroup === g.id).toString()"
      @mouseenter="openGroupMenu(g)"
      @mouseleave="scheduleHoverClose"
      @click.stop="handleGroupClick(g)"
    >
      <span>{{ g.label }}</span>
    </button>
  </nav>

  <teleport to="body">
    <transition name="section-menu-fade">
      <div
        v-if="openGroup"
        ref="panel"
        class="section-menu-panel"
        :class="{ 'section-menu-panel-pinned': pinnedGroup === openGroup }"
        :style="panelStyle"
        role="menu"
        :aria-label="openGroupLabel"
        @mouseenter="cancelHoverClose"
        @mouseleave="scheduleHoverClose"
      >
        <button
          v-for="c in openChildren"
          :key="c.id"
          type="button"
          class="section-menu-entry"
          role="menuitem"
          @click="goToEntry(c.id)"
        >
          <span class="section-menu-entry-top">
            <span class="section-menu-entry-title">{{ c.label }}</span>
            <span v-if="c.date" class="section-menu-entry-date">{{ c.date }}</span>
          </span>
          <span v-if="c.subtitle" class="section-menu-entry-subtitle">{{ c.subtitle }}</span>
        </button>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { settleScrollToId } from '../utils/anchorLanding';

export default {
  name: 'SectionMenu',
  props: {
    // Group definitions come from the page (simplified labels kept by design);
    // the entries under each group are populated automatically from the DOM.
    groups: {
      type: Array,
      default: () => []
    },
    // Optional prebuilt entries per group, keyed by the group key. Pages with
    // flat timelines (no group sections in the DOM) pass this instead of
    // relying on DOM scraping.
    children: {
      type: Object,
      default: () => ({})
    },
    label: {
      type: String,
      default: 'Page sections'
    }
  },
  data() {
    return {
      sections: [],
      openGroup: '',
      pinnedGroup: '',
      panelStyle: null
    };
  },
  computed: {
    openGroupLabel() {
      const g = this.sections.find((x) => x.id === this.openGroup);
      return g ? g.label : '';
    },
    openChildren() {
      const g = this.sections.find((x) => x.id === this.openGroup);
      return g ? g.children || [] : [];
    }
  },
  mounted() {
    this.buildSections();
    // The menu follows the page content: rebuild whenever entries are added,
    // removed, or filtered by search.
    this._observer = new MutationObserver(() => this.scheduleRebuild());
    const root = document.querySelector('.content-container') || document.body;
    this._observer.observe(root, { childList: true, subtree: true });
    this._onAnyScroll = () => this.unpin();
    this._onResize = () => {
      if (this.openGroup) this.positionPanel();
    };
    window.addEventListener('scroll', this._onAnyScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    this._onDocClick = (event) => {
      const menu = this.$refs.menu;
      const panel = this.$refs.panel;
      if (menu && menu.contains(event.target)) return;
      if (panel && panel.contains(event.target)) return;
      this.unpin();
    };
    document.addEventListener('click', this._onDocClick);
  },
  unmounted() {
    if (this._observer) this._observer.disconnect();
    if (this._rebuildTimer) window.clearTimeout(this._rebuildTimer);
    if (this._hoverCloseTimer) window.clearTimeout(this._hoverCloseTimer);
    window.removeEventListener('scroll', this._onAnyScroll);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('click', this._onDocClick);
  },
  methods: {
    buildSections() {
      const next = this.groups.map((g) => {
        const provided = this.children[g.key] || [];
        const section = document.getElementById(g.id);
        return {
          id: g.id,
          key: g.key,
          label: g.label,
          title: g.title || g.label,
          children: provided.length
            ? provided
            : (section ? [...section.querySelectorAll('.timeline-event')]
              .map((el) => {
                const h3 = el.querySelector('h3');
                const subtitle = [...el.querySelectorAll('h4')]
                  .find((h) => !/^associated\s+with/i.test(h.textContent.trim()));
                const date = el.querySelector('.timeline-event-thumbnail');
                return {
                  id: el.id || (h3 && h3.id) || '',
                  label: h3 ? h3.textContent.trim() : '',
                  subtitle: subtitle ? subtitle.textContent.trim() : '',
                  date: date ? date.textContent.trim() : '',
                };
              })
              .filter((c) => c.id && c.label) : []),
        };
      });
      this.sections = next;
      if (this.openGroup && !next.some((g) => g.id === this.openGroup)) {
        this.openGroup = '';
      }
      if (this.pinnedGroup && !next.some((g) => g.id === this.pinnedGroup)) {
        this.pinnedGroup = '';
      }
    },
    scheduleRebuild() {
      if (this._rebuildTimer) window.clearTimeout(this._rebuildTimer);
      this._rebuildTimer = window.setTimeout(() => this.buildSections(), 80);
    },
    openGroupMenu(g) {
      this.cancelHoverClose();
      if (this.pinnedGroup) return;
      if (this.openGroup === g.id) return;
      this.openGroup = g.id;
      this.$nextTick(this.positionPanel);
    },
    handleGroupClick(g) {
      this.cancelHoverClose();
      this.pinnedGroup = g.id;
      this.openGroup = g.id;
      this.$nextTick(this.positionPanel);
    },
    scheduleHoverClose() {
      if (this.pinnedGroup) return;
      this.cancelHoverClose();
      this._hoverCloseTimer = window.setTimeout(() => {
        this._hoverCloseTimer = null;
        if (!this.pinnedGroup) this.openGroup = '';
      }, 120);
    },
    cancelHoverClose() {
      if (!this._hoverCloseTimer) return;
      window.clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = null;
    },
    unpin() {
      this.cancelHoverClose();
      this.pinnedGroup = '';
      this.openGroup = '';
    },
    positionPanel() {
      const nav = this.$refs.menu;
      if (!nav) return;
      const nr = nav.getBoundingClientRect();
      this.panelStyle = {
        left: `${Math.round(nr.left)}px`,
        top: `${Math.round(nr.bottom)}px`,
        width: `${Math.round(nr.width)}px`
      };
    },
    goToEntry(id) {
      this.unpin();
      // The entry may live in a collapsed group; the page expands it on the
      // first attempt, so keep retrying until the element has rendered.
      let tries = 0;
      const land = () => {
        if (settleScrollToId(id)) return;
        if (tries++ < 25) window.setTimeout(land, 100);
      };
      land();
    }
  }
};
</script>

<style scoped>
.section-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.15rem;
  width: auto;
  margin: 1.35rem 0 0.4rem;
  padding: 0.35rem;
  border: 0;
  border-radius: 0;
  background: rgba(218, 218, 224, 0.6);
  box-shadow: none;
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  overflow-x: auto;
  scrollbar-width: none;
  font-family: "Quattrocento Sans", sans-serif;
}
.section-menu::-webkit-scrollbar {
  display: none;
}

.section-menu-group {
  position: relative;
  display: inline-flex;
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0.2rem 0.55rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #66666a;
  font: inherit;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
  text-align: center;
  cursor: pointer;
  transition: color 150ms ease, background-color 150ms ease;
}
.section-menu-group + .section-menu-group::before {
  content: "";
  position: absolute;
  left: -0.09rem;
  top: 50%;
  width: 1px;
  height: 15px;
  transform: translateY(-50%);
  background: linear-gradient(180deg, transparent, currentColor 40%, currentColor 60%, transparent);
  opacity: 0.45;
  pointer-events: none;
}
.section-menu-group:hover,
.section-menu-group:focus-visible {
  color: #242426;
  background: rgba(0, 0, 0, 0.065);
}
.section-menu-group.open {
  color: #242426;
  background: rgba(0, 0, 0, 0.065);
}
.section-menu-group.pinned,
.section-menu-group.pinned:hover,
.section-menu-group.pinned:focus-visible {
  color: #1f1f22;
  background: rgba(0, 0, 0, 0.1);
}
.section-menu-group:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.section-menu-panel {
  position: fixed;
  z-index: 2000;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 6px 4px;
  border: 0;
  border-radius: 0;
  background: rgba(250, 250, 251, 0.94);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.14);
  backdrop-filter: blur(14px) saturate(130%);
  -webkit-backdrop-filter: blur(14px) saturate(130%);
}
.section-menu-panel-pinned {
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.18),
    inset 0 0 0 0.5px rgba(0, 0, 0, 0.06);
}
.section-menu-entry {
  width: 100%;
  box-sizing: border-box;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #585858;
  font: inherit;
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.92rem;
  line-height: 1.4;
  text-align: left;
  padding: 9px 12px;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease;
}
.section-menu-entry-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
}
.section-menu-entry-title {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.section-menu-entry-subtitle {
  display: block;
  margin-top: 2px;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.7rem;
  line-height: 1.35;
  color: #8f8f8f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.section-menu-entry-date {
  flex: 0 0 auto;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  letter-spacing: 0.04em;
  color: #9a9a9a;
  white-space: nowrap;
}
.section-menu-entry:hover,
.section-menu-entry:focus-visible {
  color: #2e2e2e;
  background: rgba(118, 118, 128, 0.09);
}

:global(body.dark-mode .section-menu) {
  background: rgba(72, 72, 78, 0.64);
}
:global(body.dark-mode .section-menu-group) {
  color: #b9b9bd;
}
:global(body.dark-mode .section-menu-group:hover),
:global(body.dark-mode .section-menu-group:focus-visible) {
  color: #f1f1f2;
  background: rgba(255, 255, 255, 0.1);
}
:global(body.dark-mode .section-menu-group.open) {
  color: #f1f1f2;
  background: rgba(255, 255, 255, 0.1);
}
:global(body.dark-mode .section-menu-group.pinned),
:global(body.dark-mode .section-menu-group.pinned:hover),
:global(body.dark-mode .section-menu-group.pinned:focus-visible) {
  color: #f5f5f6;
  background: rgba(255, 255, 255, 0.14);
}
:global(body.dark-mode .section-menu-panel) {
  background: rgba(44, 44, 46, 0.95);
}
:global(body.dark-mode .section-menu-panel-pinned) {
  box-shadow:
    0 18px 44px rgba(0, 0, 0, 0.42),
    inset 0 0 0 0.5px rgba(255, 255, 255, 0.1);
}
:global(body.dark-mode .section-menu-entry) {
  color: #c4c4c4;
}
:global(body.dark-mode .section-menu-entry:hover),
:global(body.dark-mode .section-menu-entry:focus-visible) {
  color: #f2f2f2;
  background: rgba(255, 255, 255, 0.07);
}
:global(body.dark-mode .section-menu-entry-date) {
  color: #888;
}
:global(body.dark-mode .section-menu-entry-subtitle) {
  color: #a0a0a0;
}

:global(html.theme-transitioning .section-menu),
:global(body.theme-transitioning .section-menu) {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .section-menu-group),
:global(body.theme-transitioning .section-menu-group) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .section-menu-panel),
:global(body.theme-transitioning .section-menu-panel) {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .section-menu-entry),
:global(body.theme-transitioning .section-menu-entry),
:global(html.theme-transitioning .section-menu-entry-title),
:global(body.theme-transitioning .section-menu-entry-title),
:global(html.theme-transitioning .section-menu-entry-date),
:global(body.theme-transitioning .section-menu-entry-date),
:global(html.theme-transitioning .section-menu-entry-subtitle),
:global(body.theme-transitioning .section-menu-entry-subtitle) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease;
}

.section-menu-fade-enter-active {
  transition: opacity 0.16s ease, transform 0.22s cubic-bezier(0.23, 1, 0.32, 1);
}
.section-menu-fade-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}
.section-menu-fade-leave-active {
  transition: opacity 0.12s ease;
}
.section-menu-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .section-menu-fade-enter-active,
  .section-menu-fade-leave-active {
    transition: opacity 0.1s ease;
  }
  .section-menu-fade-enter-from {
    transform: none;
  }
}
</style>
